<script>
  // The want page's People tab (#222 slice F; spec
  // docs/superpowers/specs/2026-11-02-wantlist-redesign-design.md "People"):
  // who holds the most of the page's wants, a list only. A view over the
  // want page's own read: WantPage reads it with the group picker's Reach
  // (layout/GroupPicker, in the top bar), so `entries` already carry only
  // the holders in that scope; `failed` says that read was refused, and
  // `stale` that the picker has moved on and its read has not landed yet (the
  // list dims and is marked busy rather than pass for the new selection's).
  // logic/want-people-logic.js pivots the holders into people.
  import { authState } from '../../lib/stores/auth.svelte.js';
  import { groupsState } from '../../lib/stores/groups.svelte.js';
  import { browseState, selectedServerGroupId } from '../../lib/stores/browse.svelte.js';
  import { followLink } from '../../lib/stores/router.svelte.js';
  import { buildUserPageUrl } from '../../lib/logic/user-page-logic.js';
  import { PEOPLE_CARDS_PHONE, PEOPLE_PHONE_COLUMNS } from '../../lib/logic/config.js';
  import {
    pivotPeople,
    representativeFew,
    sharedGroupsOf,
  } from '../../lib/logic/want-people-logic.js';
  import { getCardImageUrl } from '../../lib/services/card-utils.js';
  import Avatar from '../shared/Avatar.svelte';
  import GroupChip from '../shared/GroupChip.svelte';
  import CardTile from '../shared/CardTile.svelte';
  import CardPills from '../shared/CardPills.svelte';

  /** @type {{ entries: import('../../lib/types.js').WantEntry[], failed?: boolean, stale?: boolean }} */
  let { entries, failed = false, stale = false } = $props();

  // "Mine Only" has no entry in this page's picker, but the selection is
  // shared with the browse pages and can arrive set.
  const mineOnly = $derived(browseState.selectedGroupId === 'mine');
  const pickedGroupId = $derived(selectedServerGroupId());
  const people = $derived(
    pivotPeople(entries, { viewerUid: authState.user?.uid ?? null, mineOnly })
  );

  /** @param {MouseEvent} e @param {string} handle */
  const toUser = (e, handle) => followLink(e, 'user', { handle });
  /** @param {MouseEvent} e @param {string} url */
  const toBinder = (e, url) => followLink(e, 'binder', { binderUrl: url, shortPath: url });
</script>

{#snippet more(/** @type {string | null} */ handle, /** @type {number} */ n, cls)}
  <li class={cls}>
    {#if handle}
      <a class="more" href={buildUserPageUrl(handle)} onclick={(e) => toUser(e, handle)}>+{n}</a>
    {:else}
      <span class="more">+{n}</span>
    {/if}
  </li>
{/snippet}

<section
  class="people"
  aria-label="People"
  aria-busy={stale}
  style:--phone-cols={PEOPLE_PHONE_COLUMNS}
>
  {#if failed}
    <p class="empty">Couldn't load the people in this group.</p>
  {:else if people.length === 0}
    <p class="empty">
      {mineOnly
        ? "Your binders don't have any of these cards."
        : 'Nobody here has any of these cards yet.'}
    </p>
  {:else}
    <ol class="board" class:stale>
      {#each people as p (p.uid)}
        {@const few = representativeFew(p.cards)}
        <li class="person">
          <div class="p-head">
            {#snippet identity()}
              <Avatar id={p.uid} name={p.name} photoUrl={groupsState.memberUidToPhoto?.[p.uid]} />
              <span class="p-name">{p.name}</span>
            {/snippet}
            {#if p.handle}
              <a class="who" href={buildUserPageUrl(p.handle)} onclick={(e) => toUser(e, p.handle)}
                >{@render identity()}</a
              >
            {:else}
              <span class="who">{@render identity()}</span>
            {/if}
            <ul class="chips">
              {#each sharedGroupsOf(p.sharedGroupIds, groupsState.userGroups) as g (g.groupId)}
                <li><GroupChip group={g} size="sm" active={g.groupId === pickedGroupId} /></li>
              {/each}
            </ul>
          </div>
          <ul class="strip">
            {#each few.shown as card, i (card.key)}
              {@const src = card.imageId
                ? getCardImageUrl({ scryfallId: card.imageId }, 'normal')
                : null}
              <li class="strip-card" class:desktop-only={i >= PEOPLE_CARDS_PHONE}>
                {#snippet tile()}
                  <CardTile name={card.name} imageUrl={src}>
                    {#snippet overlay()}
                      <CardPills price={card.price} foil={card.foil} quantity={card.quantity} />
                    {/snippet}
                  </CardTile>
                {/snippet}
                {#if card.binderUrl}
                  {@const url = card.binderUrl}
                  <a class="media" href={url} onclick={(e) => toBinder(e, url)}>{@render tile()}</a>
                {:else}
                  <div class="media">{@render tile()}</div>
                {/if}
              </li>
            {/each}
            {#if few.more.desktop > 0}{@render more(
                p.handle,
                few.more.desktop,
                'desktop-only'
              )}{/if}
            {#if few.more.phone > 0}{@render more(p.handle, few.more.phone, 'phone-only')}{/if}
          </ul>
        </li>
      {/each}
    </ol>
  {/if}
</section>

<style>
  .board {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: opacity var(--dur-fast) var(--ease-standard);
  }
  /* The previous selection's people while the new one's read is in flight. */
  .board.stale {
    opacity: 0.5;
  }
  .person {
    border: 1px solid var(--color-border);
    border-radius: 12px;
    background: var(--color-bg-surface);
  }
  .p-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 12px;
    padding: 10px 14px 8px;
  }
  .who {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    color: inherit;
    text-decoration: none;
  }
  a.who:hover .p-name {
    text-decoration: underline;
  }
  .p-name {
    font-weight: 700;
    font-size: 15px;
    color: var(--color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chips {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  /* A representative few of what they hold, wrapping onto new lines. */
  .strip {
    list-style: none;
    margin: 0;
    padding: 3px 14px 12px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 12px 10px;
  }
  .strip > li:not(.strip-card) {
    align-self: center;
    justify-self: start;
  }
  .media {
    display: block;
    color: inherit;
    text-decoration: none;
  }
  /* "+N": the rest of what they hold, counted, not listed. */
  .more {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--color-accent-bg);
    font-size: 13px;
    font-weight: 700;
    color: var(--color-text-secondary);
    text-decoration: none;
  }
  a.more:hover {
    background: var(--color-border);
  }
  a.more:active {
    transform: scale(0.98);
  }
  .phone-only {
    display: none;
  }
  .empty {
    color: var(--color-text-tertiary);
    text-align: center;
    margin-top: 2rem;
  }

  @media (max-width: 768px) {
    .p-head {
      padding: 10px 12px 8px;
    }
    /* Two full rows of the phone few: PEOPLE_CARDS_PHONE = columns x rows. */
    .strip {
      padding: 3px 12px 12px;
      grid-template-columns: repeat(var(--phone-cols), minmax(0, 1fr));
    }
    .desktop-only {
      display: none;
    }
    .phone-only {
      display: block;
    }
  }
</style>
